/* eslint-disable zeffy-linter-frontend/no-classname-prop -- crash fallback must not depend on MUI/emotion, so it uses plain CSS classes */
import * as React from "react";

import { isDevelopment, isTest } from "@/common/constants/env";
import { captureDatadogFrontendCrash } from "@/common/helpers/datadog";
import { retryPageLoading } from "@/common/helpers/retryPageLoading";
import { ContainerLoader } from "@/components/Loaders/ContainerLoader";

interface ErrorHandlerProps {
  children: React.ReactNode;
}

interface ErrorBoundaryProps {
  handleError: (error: unknown, info: unknown) => void;
  children?: React.ReactNode;
}

export interface ErrorBoundaryContent {
  title: string;
  descriptionParagraphs: [string, string];
}

// The error boundary is the last line of defense: it can render on any route
// (including public canvas pages that never load the dashboard i18n namespace)
// and even when the i18n store is empty mid-deploy, so copy is hardcoded and
// both languages are shown at once, never depending on i18n being loaded.
export const ERROR_BOUNDARY_CONTENT: { english: ErrorBoundaryContent; french: ErrorBoundaryContent } = {
  english: {
    title: "We’re sorry, an error has occurred within the application.",
    descriptionParagraphs: [
      "We’ve been notified of the error and we’re actively working to resolve it.",
      "You can email us at contact@zeffy.com to provide us with more details about what you were doing on the application when you received the error.",
    ],
  },
  french: {
    title: "Nous sommes désolés, une erreur s'est produite dans l'application.",
    descriptionParagraphs: [
      "Nous avons été informés de l'erreur et nous travaillons activement à la résoudre.",
      "Vous pouvez nous envoyer un e-mail à contact@zeffy.com pour nous fournir plus de détails sur ce que vous faisiez dans l'application lorsque vous avez reçu l'erreur.",
    ],
  },
};

const CONTACT_EMAIL = "contact@zeffy.com";

// Fallback styles are plain CSS in a <style> tag: MUI/emotion may be the very
// thing that crashed, so this screen must not depend on any styling library.
const ERROR_BOUNDARY_FALLBACK_CSS = `
.zf-error-fallback {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 100px;
  min-height: 100vh;
  padding: 40px;
  background-color: #ffffff;
  font-family: Roboto, "Helvetica Neue", Arial, sans-serif;
}
@supports (min-height: 100dvh) {
  .zf-error-fallback {
    min-height: 100dvh;
  }
}
.zf-error-fallback *,
.zf-error-fallback *::before,
.zf-error-fallback *::after {
  box-sizing: inherit;
  margin: 0;
  padding: 0;
}
.zf-error-fallback__illustration {
  width: 335.5px;
  height: 200px;
  flex-shrink: 0;
}
.zf-error-fallback__content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 40px;
  max-width: 642px;
}
.zf-error-fallback__block {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}
.zf-error-fallback__heading {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.zf-error-fallback__title {
  color: #0f0e5b;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  letter-spacing: 0.03px;
}
.zf-error-fallback__text {
  color: #57568c;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.5px;
}
.zf-error-fallback__link {
  color: inherit;
  text-decoration: underline;
}
.zf-error-fallback__divider {
  width: 398.65px;
  max-width: 100%;
  height: 1px;
  border: none;
  border-radius: 10px;
  background-color: #e5e5ee;
}
@media (max-width: 1200px) {
  .zf-error-fallback {
    flex-direction: column;
    gap: 48px;
    text-align: left;
  }
}
`;

export const ErrorBoundaryFallback: React.FunctionComponent = () => {
  const renderBlock = (content: ErrorBoundaryContent) => {
    const [firstParagraph, contactParagraph] = content.descriptionParagraphs;
    const [beforeEmail, afterEmail] = contactParagraph.split(CONTACT_EMAIL);

    return (
      <div className="zf-error-fallback__block">
        <div className="zf-error-fallback__heading">
          <p className="zf-error-fallback__title">{content.title}</p>
          <p className="zf-error-fallback__text">{firstParagraph}</p>
        </div>
        <p className="zf-error-fallback__text">
          {beforeEmail}
          <a className="zf-error-fallback__link" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
          {afterEmail}
        </p>
      </div>
    );
  };

  return (
    <div className="zf-error-fallback">
      <style>{ERROR_BOUNDARY_FALLBACK_CSS}</style>
      <img
        className="zf-error-fallback__illustration"
        src="/images/error-boundary-illustration.svg"
        alt=""
        aria-hidden="true"
      />
      <div className="zf-error-fallback__content">
        {renderBlock(ERROR_BOUNDARY_CONTENT.english)}
        <hr className="zf-error-fallback__divider" />
        {renderBlock(ERROR_BOUNDARY_CONTENT.french)}
      </div>
    </div>
  );
};

export const ErrorHandler: React.FunctionComponent<React.PropsWithChildren<ErrorHandlerProps>> = ({ children }) => {
  const handleError = (error: unknown) => {
    captureDatadogFrontendCrash(error);
  };

  return <ErrorBoundary handleError={handleError}>{children}</ErrorBoundary>;
};

class ErrorBoundary extends React.Component<ErrorBoundaryProps, { hasError: boolean; isLoading: boolean }> {
  constructor(props: Readonly<ErrorBoundaryProps>) {
    super(props);
    this.state = { hasError: false, isLoading: false };
  }
  componentDidCatch(error: unknown, info: unknown) {
    // eslint-disable-next-line no-console
    console.error(error, info);
    if (isDevelopment || isTest) {
      return;
    }
    const { willRefresh } = retryPageLoading();
    if (willRefresh) {
      return this.setState({ hasError: false, isLoading: true });
    }
    this.setState({ hasError: true, isLoading: false });
    if (process.env.NODE_ENV === "production") {
      this.props.handleError(error, info);
    }
  }
  render() {
    if (this.state.isLoading) {
      return <ContainerLoader fullPage />;
    }
    if (this.state.hasError) {
      return <ErrorBoundaryFallback />;
    }
    return this.props.children;
  }
}
